import type { ReactNode } from "react";
import { Plus } from "../icons/icons";
import { cx } from "../lib/cx";
import { useHoistedPageTitle } from "./page-title";

export interface ListHeaderProps {
	/** Section title ("Responsible organisations"). */
	title: ReactNode;
	/** Already-worded count beside the title ("22 organisations") — the app pluralises. */
	count?: ReactNode;
	/** One subtle line under the title. Optional; keep it short. */
	description?: ReactNode;
	/**
	 * The page's main action ("Add country"). Omit `onAction` to hide it (the
	 * viewer may not create). At h1 level it is the designer's red CTA (her
	 * "Register new person": one per page); in an `as="h2"` section it is navy.
	 */
	actionLabel?: ReactNode;
	onAction?: () => void;
	actionIcon?: ReactNode;
	/** Override the action's colour. Default: `accent` (red) at h1, `primary` (navy) at h2. */
	actionTone?: "accent" | "primary";
	/** Secondary buttons, drawn before the primary one. Use `ListHeaderButton`. */
	secondary?: ReactNode;
	/** Heading level. Default h1 (the page's title). */
	as?: "h1" | "h2";
	className?: string;
}

/**
 * The list page's title row (prototype `.sec__h`): the title with a grey
 * count beside it, an optional subtle line under, and the navy action on the
 * right. Wraps under on phones.
 *
 * At the page's h1 level inside an app that provides a `PageTitleProvider`
 * (the GMS shell's top bar), the title goes UP to the top bar and this row
 * keeps only the count, the description and the actions — her `.pgh`: no
 * second big title under the bar. `as="h2"` (a section inside a page) always
 * draws its own title.
 */
export function ListHeader({ title, count, description, actionLabel, onAction, actionIcon, actionTone, secondary, as: H = "h1", className }: ListHeaderProps) {
	const hoisted = useHoistedPageTitle(title, H === "h1");
	return (
		<header className={cx("flex flex-wrap items-center justify-between gap-x-[1rem] gap-y-[0.625rem]", className)}>
			<div className="min-w-0 flex-[1_1_18rem]">
				{hoisted ? (
					count !== undefined && <p className="text-[0.875rem] leading-normal text-gms-ink-2 tabular-nums">{count}</p>
				) : (
					// Her `.sec__h h2`: 15.5px semibold, the count muted beside it.
					<H className="flex flex-wrap items-baseline gap-x-[0.5rem] gap-y-[0.125rem] text-[0.96875rem] leading-tight font-semibold text-gms-navy-ink">
						<span>{title}</span>
						{count !== undefined && <span className="text-[0.78125rem] font-normal text-gms-ink-3 tabular-nums">{count}</span>}
					</H>
				)}
				{description && <p className="mt-[0.25rem] max-w-[48rem] text-[0.8125rem] leading-snug text-gms-ink-3">{description}</p>}
			</div>
			{(secondary || (onAction && actionLabel)) && (
				// Phones: the row takes the full width and the main action leads it, full width (her `.rh__reg`).
				<div className="flex flex-wrap items-center gap-[0.5rem] max-[44.999rem]:w-full">
					{secondary}
					{onAction && actionLabel && (
						<ListHeaderButton
							tone={actionTone ?? (H === "h1" ? "accent" : "primary")}
							onClick={onAction}
							icon={actionIcon ?? <Plus />}
							className="max-[44.999rem]:order-first max-[44.999rem]:flex-[1_1_100%]"
						>
							{actionLabel}
						</ListHeaderButton>
					)}
				</div>
			)}
		</header>
	);
}

export interface ListHeaderButtonProps {
	children: ReactNode;
	onClick?: () => void;
	icon?: ReactNode;
	/**
	 * `accent` = the red CTA (`.btn--red`); `primary` = navy fill (`.btn`);
	 * `line` = white with a navy label (`.btn--line`).
	 */
	tone?: "accent" | "primary" | "line";
	disabled?: boolean;
	title?: string;
	className?: string;
}

/** Her header buttons (`.rh__a .btn`) — 42px tall, 8px radius, 14px semibold, 14px in. */
export function ListHeaderButton({ children, onClick, icon, tone = "line", disabled, title, className }: ListHeaderButtonProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			title={title}
			className={cx(
				"inline-flex min-h-[2.625rem] cursor-pointer items-center justify-center gap-[0.5rem] rounded-[0.5rem] border px-[0.875rem] text-[0.875rem] font-semibold whitespace-nowrap outline-none transition-colors focus-visible:ring-2 focus-visible:ring-gms-navy/40 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 [&>svg]:size-[1rem]",
				tone === "accent"
					? "border-gms-red bg-gms-red text-white hover:enabled:border-[#c8161d] hover:enabled:bg-[#c8161d]"
					: tone === "primary"
						? "border-gms-navy bg-gms-navy text-white hover:enabled:border-gms-navy-lift hover:enabled:bg-gms-navy-lift"
						: "border-gms-line-2 bg-base-100 text-gms-navy-ink hover:enabled:border-gms-navy [&>svg]:text-gms-ink-2",
				className,
			)}
		>
			{icon}
			{children}
		</button>
	);
}
